import { forwardRef } from 'react';
import type { ButtonHTMLAttributes } from 'react';
import { cn } from '@/utils/cn';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'primary', size = 'md', ...props }, ref) => {
    return (
      <button
        ref={ref}
        className={cn(
          'inline-flex items-center justify-center rounded-md font-medium transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/50 focus-visible:ring-offset-2 focus-visible:ring-offset-dark-base',
          'disabled:pointer-events-none disabled:opacity-50 disabled:cursor-not-allowed',
          {
            'bg-accent text-dark-base hover:bg-accent-hover': variant === 'primary',
            'bg-dark-elevated-2 text-text-primary border border-border-subtle hover:bg-dark-card': variant === 'secondary',
            'border border-border-strong text-text-primary hover:bg-dark-elevated-2': variant === 'outline',
            'text-text-secondary hover:text-text-primary hover:bg-dark-elevated-2': variant === 'ghost',
            'bg-danger text-text-inverse hover:bg-danger/85': variant === 'danger',
          },
          {
            'h-8 px-3 text-sm': size === 'sm',
            'h-11 px-4': size === 'md',
            'h-12 px-6 text-lg': size === 'lg',
          },
          className
        )}
        {...props}
      />
    );
  }
);

Button.displayName = 'Button';

export default Button;
